import {
  Box,
  Button,
  Checkbox,
  Divider,
  Group,
  InputWrapper,
  NumberInput,
  Pill,
  Radio,
  Stack,
  Text,
  TextInput,
} from '@mantine/core'
import { useForm, zodResolver } from '@mantine/form'
import { IconMinus, IconPlus } from '@tabler/icons-react'
import { useCallback, useState } from 'react'
import {
  BerthFormSchema,
  BerthFormType,
  emptyBerthFormSchema,
} from '../models/marina'
import { useQueryClient } from '@tanstack/react-query'
import { useAddBerths } from '../api/marina'
import { CalendarStore } from './Calendar'
import { useMediaQuery } from '@mantine/hooks'

interface Props {
  marinaId?: string
  onCancel: () => void
}

export function BerthForm({ marinaId, onCancel }: Props) {
  const [pricing, setPricing] = useState<'flat_fee' | 'per_meter'>('per_meter')
  const [newBerth, setNewBerth] = useState<string>('')
  const queryClient = useQueryClient()
  const { mutate: createNewBerths, isPending } = useAddBerths()
  const isSmallScreen = useMediaQuery('(max-width: 640px)')

  const form = useForm<BerthFormType>({
    initialValues: emptyBerthFormSchema,
    validate: zodResolver(BerthFormSchema),
  })

  const handleSubmit = useCallback(
    async (values: BerthFormType) => {
      if (marinaId && !isNaN(parseInt(marinaId, 10))) {
        createNewBerths(
          {
            berthsData: values,
            marinaId: parseInt(marinaId, 10),
            pricing: pricing,
          },
          {
            onSuccess: (data) => {
              let ids = data.berth_ids
              queryClient.invalidateQueries({ queryKey: ['berths-by-period'] })
              CalendarStore.update((s) => {
                s.visibleBerths = Array.from(
                  new Set([...s.visibleBerths, ...ids])
                )
              })
              form.reset()
              onCancel()
            },
          }
        )
      }
    },
    [marinaId, queryClient, pricing, createNewBerths, onCancel, form]
  )

  return (
    <form onSubmit={form.onSubmit(handleSubmit)}>
      <Stack>
        <InputWrapper
          id="min_length"
          label="Boat size (length in meters)"
          error={[form.errors.min_length, form.errors.max_length]
            .filter(Boolean)
            .join(' ')}
        >
          {isSmallScreen ? (
            <Stack align="flex-start" mb={5}>
              <NumberInput
                flex="1"
                placeholder="Min"
                description="Min size"
                required
                min={0}
                maw={310}
                w="100%"
                allowDecimal={false}
                {...form.getInputProps('min_length', { withError: false })}
              />
              <NumberInput
                placeholder="Max"
                description="Max size"
                flex="1"
                required
                min={0}
                maw={310}
                w="100%"
                allowDecimal={false}
                {...form.getInputProps('max_length', { withError: false })}
              />
            </Stack>
          ) : (
            <Group align="flex-end" mb={5}>
              <NumberInput
                flex="1"
                placeholder="Min"
                description="Min size"
                required
                min={0}
                allowDecimal={false}
                {...form.getInputProps('min_length', { withError: false })}
              />
              <Box h={32}>
                <IconMinus />
              </Box>
              <NumberInput
                placeholder="Max"
                description="Max size"
                flex="1"
                required
                min={0}
                allowDecimal={false}
                {...form.getInputProps('max_length', { withError: false })}
              />
            </Group>
          )}
        </InputWrapper>

        <Checkbox.Group
          value={form.values.boat_types}
          onChange={(value) => form.setFieldValue('boat_types', value)}
          label="Compatible boat types"
        >
          <Group mt="xs">
            <Checkbox value="motorboat" label="Motorboat" />
            <Checkbox value="sailboat" label="Sailboat" />
            <Checkbox value="catamaran" label="Catamaran" />
          </Group>
        </Checkbox.Group>

        <Divider />

        <InputWrapper
          id="price"
          label="Price"
          description="Select which pricing method will be used for these berths"
        >
          <Radio.Group
            value={pricing}
            onChange={(value) => setPricing(value as 'per_meter' | 'flat_fee')}
          >
            <Stack pt={20} gap="xs">
              <Group>
                <NumberInput
                  hideControls
                  disabled={pricing !== 'per_meter'}
                  placeholder="0.00"
                  w={80}
                  {...form.getInputProps('price_per_meter')}
                />
                <Radio value="per_meter" label="Price per meter per day (€)" />
              </Group>

              <Group>
                <NumberInput
                  hideControls
                  placeholder="0.00"
                  disabled={pricing !== 'flat_fee'}
                  w={80}
                  {...form.getInputProps('price_flat_fee')}
                />
                <Radio value="flat_fee" label="Flat fee per day (€)" />
              </Group>
            </Stack>
          </Radio.Group>
        </InputWrapper>

        <Divider />

        <Stack gap={0}>
          <Group align="flex-end">
            <TextInput
              label="Berths"
              description="Press enter to add"
              value={newBerth}
              onChange={(e) => setNewBerth(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && newBerth.trim() !== '') {
                  e.preventDefault()
                  form.setFieldValue('berths', [
                    ...form.values.berths,
                    newBerth.trim(),
                  ])
                  setNewBerth('')
                }
              }}
            />
            <Button
              variant="outline"
              p={10}
              onClick={() => {
                if (newBerth.trim() !== '') {
                  form.setFieldValue('berths', [
                    ...form.values.berths,
                    newBerth.trim(),
                  ])
                  setNewBerth('')
                }
              }}
            >
              <IconPlus />
            </Button>
          </Group>
          {form.errors.berths && (
            <Text c="red" size="xs" mt={5}>
              {form.errors.berths}
            </Text>
          )}
        </Stack>

        <Group gap="xs">
          {form.values.berths.map((berth, index) => (
            <Pill
              key={index}
              withRemoveButton
              onRemove={() => {
                form.setFieldValue(
                  'berths',
                  form.values.berths.filter((_, i) => i !== index)
                )
              }}
            >
              {berth}
            </Pill>
          ))}
        </Group>

        <Divider />
        <Group justify="flex-end" mt="md">
          <Button variant="outline" size="md" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            size="md"
            loading={isPending}
            disabled={isPending}
          >
            Add Berths
          </Button>
        </Group>
      </Stack>
    </form>
  )
}
